import moment from 'moment';

// ─── Date formatters ─────────────────────────────────────────────────────────

/** "DD/MM/YYYY" — standard UI display date */
export const formatDisplayDate = (date) => moment(date).format('DD/MM/YYYY');

/** "YYYY-MM-DD" — API request date */
export const formatApiDate = (date) => moment(date).format('YYYY-MM-DD');

/** "MM-YYYY" — month filter (attendance / sessions) */
export const formatMonthYear = (date) => moment(date).format('MM-YYYY');

/** "YYYY/MM/DD" */
export const formatSlashDate = (date) => moment(date).format('YYYY/MM/DD');

/** "DD MMM YYYY" — compact display (e.g. "25 Feb 2026") */
export const formatFullDate = (date) => moment(date).format('DD MMM YYYY');

/** "dddd, DD MMMM, YYYY" — long display (e.g. "Wednesday, 25 February, 2026") */
export const formatLongDate = (date) => moment(date).format('dddd, DD MMMM, YYYY');

/** "ddd, MMM DD, YYYY" — short weekday display */
export const formatShortWeekDate = (date) => moment(date).format('ddd, MMM DD, YYYY');

/** Format time based on clinic's time_format setting (12 or 24 hour) */
export const formatTime = (time, format) => {
  if (!time) return '';
  // Try to get format from localStorage setting if not passed
  const tf = format || (() => {
    try {
      const settings = JSON.parse(localStorage.getItem('appointment_settings') || '{}');
      return settings.time_format || '24';
    } catch { return '24'; }
  })();
  if (tf === '12') return moment(time, 'HH:mm:ss').format('hh:mm A');
  return moment(time, 'HH:mm:ss').format('HH:mm');
};

// ─── Currency helper ─────────────────────────────────────────────────────────

/** Get currency symbol from Redux store (reads from localStorage as fallback) */
export const getCurrencySymbol = () => {
  try {
    const stored = localStorage.getItem('persist:root');
    if (stored) {
      const root = JSON.parse(stored);
      const auth = JSON.parse(root.auth || '{}');
      return auth.clinicBranding?.currency || '₹';
    }
  } catch {}
  return '₹';
};

/**
 * Amount, grouped the Indian way, with paise only when there are paise:
 * formatMoney(5000) → "5,000", formatMoney(354464.6) → "3,54,464.60".
 * A bare toLocaleString printed that second figure as "3,54,464.6", and
 * rounding to whole rupees printed a live sub-rupee balance as "0".
 */
export const formatMoney = (amount) => {
  const n = Math.round((Number(amount) || 0) * 100) / 100;
  return n % 1 === 0
    ? n.toLocaleString('en-IN')
    : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Format amount with currency symbol: formatCurrency(5000) → "₹5,000" */
export const formatCurrency = (amount, symbol) => {
  const s = symbol || getCurrencySymbol();
  const num = parseFloat(amount);
  if (isNaN(num)) return `${s}0`;
  return `${s}${formatMoney(num)}`;
};

// ─── Status helpers ───────────────────────────────────────────────────────────

/**
 * Attendance / presence status → inline style object { bg, dot }
 * Used in EmployeeProfile, EmployeeList, Dashboard
 */
export const getAttendanceStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'present': return { variant: 'success', bg: 'bg-success-50', dot: 'bg-success-500' };
    case 'absent':  return { variant: 'danger',  bg: 'bg-danger-50',  dot: 'bg-danger-500' };
    case 'leave':   return { variant: 'warning', bg: 'bg-warning-50', dot: 'bg-warning-500' };
    default:        return { variant: 'neutral', bg: 'bg-surface-hover', dot: 'bg-content-tertiary' };
  }
};

/**
 * Payment status → Tailwind class string
 * Used in allpatient, Payment, patientprofile
 */
export const getPaymentStatusClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid':           return 'text-success-700 bg-success-50';
    case 'unpaid':         return 'text-danger-700 bg-danger-50';
    case 'partially_paid': return 'text-warning-700 bg-warning-50';
    default:               return 'text-content-primary bg-surface-hover';
  }
};

// ─── Avatar color helper ──────────────────────────────────────────────────────

const AVATAR_COLORS = [
  { bgColor: '#E0F3FF', textColor: '#1A73E8' },
  { bgColor: '#FFE8EC', textColor: '#E91E63' },
  { bgColor: '#FFEECD', textColor: '#F57C00' },
  { bgColor: '#F3E7FF', textColor: '#7B1FA2' },
];

/**
 * Deterministic avatar background/text color from a string (e.g. patient name).
 * Used in allpatient.jsx
 */
export const getAvatarColors = (str) => {
  const index = str ? str.charCodeAt(0) % AVATAR_COLORS.length : 0;
  return AVATAR_COLORS[index];
};
